import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  numberWithSpacesInter,
} from '../../../helpers/formatInteger';

import { textSort } from '../../../helpers/textSort';
import './style.scss';
import CustomSortIcon from '../../../components/CustomSortIcon/CustomSortIcon';
import CustomTable from '../../../components/CustomTable/CustomTable';

export const ClientHoldings = ({
  currentPortfolio,
  cashes,
  tabs,
}) => {
  const { t } = useTranslation('');

  const [tableColumns, setTableColumns] = useState();

  const getCashInstrument = (id) => {
    const cash = cashes?.find((i) => i.ID === id);
    return cash;
  };

  const percentage = (value, isText) => {
    const percentage =
      Math.round(
        ((value * 100) /
          currentPortfolio
            ?.map((i) => i.value)
            .reduce((acc, el) => acc + el, 0)) *
          100,
      ) / 100;
    return isText ? `${percentage} %` : percentage;
  };

  const data = currentPortfolio
    ?.map((row) => {
      const {instrument_id, amount, units_number, value, portfolio_code, is_pending, current_weight_fin} = row;
      return {
        key: row.ID,
        instrument_id,
        amount,
        units_number,
        financialInstrumentName: row?.instrument_name ?? row?.name,
        cashValue: amount * getCashInstrument(instrument_id)?.latest_value_sek,
        percentage: percentage(value),
        portfolio_code,
        value,
        fee: row?.fee ?? 0,
        assetClass: row.financial_asset?.name,
        financialAssetCategory: row.financial_asset_category?.name || 'TBD',
        categoryID: row.financial_asset_category?.ID,
        is_pending,
        current_weight_fin,
      };
    })
    .sort((a, b) => b.value - a.value);

  // const total = data.reduce((acc, current) => current.value + acc, 0);

  // const getWeight = (value) => {
  //   return Math.round((value / total) * 100 * 100) / 100;
  // };

  useEffect(() => {
    if (currentPortfolio?.length) {
      const defaultColumns =
        tabs === 1
          ? [
              {
                title: t('INSTRUMENT'),
                dataIndex: 'financialInstrumentName',
                className: 'holdings-instrument',
                width: '20%',
                sorter: (a, b) =>
                  textSort(
                    a.financialInstrumentName,
                    b.financialInstrumentName,
                  ),
                sortIcon: (props) => <CustomSortIcon {...props} />,
              },
              {
                title: t('PORTFOLIO'),
                dataIndex: 'portfolio_code',
                className: 'holdings-data',
                width: '8%',
                align: 'center',
                sorter: (a, b) => a.portfolio_code - b.portfolio_code,
                sortDirections: ['descend', 'ascend'],
                sortIcon: (props) => <CustomSortIcon {...props} />,
              },
              {
                title: t('UNITS'),
                dataIndex: 'units_number',
                className: 'holdings-data unitsNumber',
                align: 'right',
                width: '10%',
                sorter: (a, b) => a.unitsNumber - b.unitsNumber,
                sortDirections: ['descend', 'ascend'],
                sortIcon: (props) => <CustomSortIcon {...props} />,
                render: (value, record) => {
                  return (!record.is_pending 
                  ? value ? numberWithSpacesInter(value, null, 4) : ''
                  : '- - -');
                },
              },
              {
                title: t('VALUE') + ' kr',
                dataIndex: 'value',
                className: 'holdings-data value',
                width: '10%',
                align: 'right',
                render: (value) => {
                  return `${numberWithSpacesInter(value, 0)}`;
                },
                sorter: (a, b) => a.value - b.value,
                sortDirections: ['descend', 'ascend'],
                sortIcon: (props) => <CustomSortIcon {...props} />,
              },
              {
                title: t('WEIGHT'),
                dataIndex: 'current_weight_fin',
                className: 'holdings-data value',
                width: '10%',
                align: 'right',
                render: (value) => {
                  return `${numberWithSpacesInter(value, 2)}%`;
                },
                sorter: (a, b) => a.value - b.value,
                sortDirections: ['descend', 'ascend'],
                sortIcon: (props) => <CustomSortIcon {...props} />,
              },
              {
                title: t('FEE'),
                dataIndex: 'fee',
                className: 'holdings-fee',
                width: '10%',
                align: 'center',
                render: (value) => {
                  return (
                    <p>
                      {numberWithSpacesInter(value, 2)}%
                    </p>
                  );
                },
                sorter: (a, b) => a.fee - b.fee,
                sortDirections: ['descend', 'ascend'],
                sortIcon: (props) => <CustomSortIcon {...props} />,
              },
              {
                title: t('ASSET_CLASS'),
                dataIndex: 'assetClass',
                width: '14%',
                sorter: (a, b) => textSort(a.assetClass, b.assetClass),
                sortIcon: (props) => <CustomSortIcon {...props} />,
                render: (value, record) => {
                  return (
                    <>
                      <p className="holdings-assetclass-cell-asset">{value}</p>
                      <p className="holdings-assetclass-cell-category">
                        {record.financialAssetCategory}
                      </p>
                    </>
                  );
                },
              },
            ]
          : tabs === 2
          ? [
            {
              title: t('INSTRUMENT'),
              dataIndex: 'instrument_id',
              className: 'holdings-instrument',
              width: '20%',
              render: (value) => {
                return getCashInstrument(value)?.name ?? '- - -'; 
              },
              sorter: (a, b) =>
                textSort(
                  getCashInstrument(a.name),
                    getCashInstrument(b.name),
                ),
              sortIcon: (props) => <CustomSortIcon {...props} />,
            },
            {
              title: t('PORTFOLIO'),
              dataIndex: 'portfolio_code',
              className: 'holdings-data',
              width: '8%',
              align: 'center',
              sorter: (a, b) => a.portfolio_code - b.portfolio_code,
              sortDirections: ['descend', 'ascend'],
              sortIcon: (props) => <CustomSortIcon {...props} />,
            },

            {
              title: t('VALUE'),
              dataIndex: 'cashValue',
              className: 'holdings-data value',
              width: '10%',
              align: 'right',
              render: (value) => {
                return `${numberWithSpacesInter(value, 0)} kr`;
              },
              sorter: (a, b) => a.value - b.value,
              sortDirections: ['descend', 'ascend'],
              sortIcon: (props) => <CustomSortIcon {...props} />,
            },
          ]
          : [];
      setTableColumns(defaultColumns);
    }
  }, [currentPortfolio]);

  return (
    <div className="client portfolio-table">
      <CustomTable
        rowKey={() => uuidv4()}
        columns={tableColumns}
        dataSource={data}
        size="middle"
        pagination={false}
      />
    </div>
  );
};

ClientHoldings.propTypes = {
  currentPortfolio: PropTypes.array,
  cashes: PropTypes.array,
  tabs: PropTypes.number,
};
